import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { ActorType } from '@models/api-calls/application/application.model';
import { PaymentVerificationData } from '@models/api-calls/payment/payment-verification-warnings.model';
import { PayerRoleEnum, PaymentMethodTypeEnum, PaymentStatusEnum, PaymentVerificationEnum } from 'app/enums/form/form.enum';
import { Observable } from 'rxjs';

const API_URL = environment.apiUrl;

export interface PaymentInformationPayload {
    initial_payment_at: string | null;
    initial_payment_day: number | null;
    initial_payment_day_details: string | null;
    draw_at: string | null;
    draw_recurring_day: number | null;
    draw_recurring_day_details: string | null;
    payment_ssd: number;
    payment_frequency: number;
    payment_date_options: string;
    has_payment_disclosures?: number;
}

export interface PayerPayload {
    role: PayerRoleEnum;
    id?: number;
    first_name?: string;
    middle_name?: string;
    last_name?: string;
    relationship?: string;
    address?: string;
    address2?: string;
    city?: string;
    country_code?: string;
    state_code?: string;
    zip_code?: string;
}

export interface PaymentMethodGatewayAch {
    ach_account_number: string;
    ach_account_type: string;
    ach_routing_number: string;
    ach_bank_name: string;
}

export interface PaymentMethodGatewayCreditCard {
    cc_card_number: string;
    cc_exp_date: string;
    cc_cvv: string;
    cc_first_name: string;
    cc_last_name: string;
    cc_avs_address: string;
    cc_avs_zip: string;
}

export interface PaymentMethodPayload {
    primary_payment_method?: PaymentMethodTypeEnum;
    secondary_payment_method?: PaymentMethodTypeEnum;
    gateway: PaymentMethodGatewayAch | PaymentMethodGatewayCreditCard;
}

export interface PolicyResponse {
    status: string;
    effective_at: string;
    is_same_day: number;
    report_dashboard: number;
    dashboard_policy_id: number;
    dashboard_client_id: number;
}

export interface VerifyPaymentResponse {
    primary_payment_verification?: PaymentVerificationEnum;
    primary_payment_status?: PaymentStatusEnum;
    primary_payment_verification_at?: string | null;
    primary_payment_verification_data?: PaymentVerificationData | null;
    secondary_payment_verification?: PaymentVerificationEnum;
    secondary_payment_status?: PaymentStatusEnum;
    secondary_payment_verification_at?: string | null;
    secondary_payment_verification_data?: PaymentVerificationData | null;
}

@Injectable({
    providedIn: 'root',
})
export class PaymentService {
    constructor(private http: HttpClient) {}

    savePaymentInformation(applicationId: number, payload: PaymentInformationPayload): Observable<unknown> {
        return this.http.patch(`${API_URL}/applications/${applicationId}/payment`, payload);
    }

    createPrimaryPayer(applicationId: number, payload: Omit<PayerPayload, 'role'>): Observable<unknown> {
        return this.http.post(`${API_URL}/applications/${applicationId}/clients`, {
            role: PayerRoleEnum.PRIMARY_PAYER,
            ...payload,
        });
    }

    updatePrimaryPayer(applicationId: number, payload: Omit<PayerPayload, 'role'>, clientId: number): Observable<unknown> {
        return this.http.patch(`${API_URL}/applications/${applicationId}/clients/${clientId}`, {
            role: PayerRoleEnum.PRIMARY_PAYER,
            ...payload,
        });
    }

    createSecondaryPayer(applicationId: number, payload: Omit<PayerPayload, 'role'>): Observable<unknown> {
        return this.http.post(`${API_URL}/applications/${applicationId}/clients`, {
            role: PayerRoleEnum.SECONDARY_PAYER,
            ...payload,
        });
    }

    updateSecondaryPayer(applicationId: number, payload: Omit<PayerPayload, 'role'>, clientId: number): Observable<unknown> {
        return this.http.patch(`${API_URL}/applications/${applicationId}/clients/${clientId}`, {
            role: PayerRoleEnum.SECONDARY_PAYER,
            ...payload,
        });
    }

    savePayerContact(applicationId: number, payload: Omit<PayerPayload, 'role'>): Observable<unknown> {
        return this.http.post(`${API_URL}/applications/${applicationId}/clients`, { role: PayerRoleEnum.PAYER_CONTACT, ...payload });
    }

    deleteClient(applicationId: number, clientId: number): Observable<unknown> {
        return this.http.delete(`${API_URL}/applications/${applicationId}/clients/${clientId}`);
    }

    addClientRole(applicationId: number, clientId: number, role: ActorType): Observable<unknown> {
        return this.http.post(`${API_URL}/applications/${applicationId}/clients/${clientId}/roles`, { role });
    }

    removeClientRole(applicationId: number, clientId: number, role: ActorType): Observable<unknown> {
        return this.http.delete(`${API_URL}/applications/${applicationId}/clients/${clientId}/roles`, { body: { role } });
    }

    createPolicy(applicationId: number): Observable<PolicyResponse> {
        return this.http.put<PolicyResponse>(`${API_URL}/applications/${applicationId}/policy`, {});
    }

    savePrimaryPaymentMethod(
        applicationId: number,
        payload: PaymentMethodPayload,
        qaForcedGiactResponseCode?: number | null,
    ): Observable<unknown> {
        return this.http.put(`${API_URL}/applications/${applicationId}/payment-methods/primary`, payload, {
            params: this.buildSavePaymentMethodParams(qaForcedGiactResponseCode),
        });
    }

    saveSecondaryPaymentMethod(
        applicationId: number,
        payload: PaymentMethodPayload,
        qaForcedGiactResponseCode?: number | null,
    ): Observable<unknown> {
        return this.http.put(`${API_URL}/applications/${applicationId}/payment-methods/secondary`, payload, {
            params: this.buildSavePaymentMethodParams(qaForcedGiactResponseCode),
        });
    }

    verifyPrimaryPaymentMethod(applicationId: number, qaForceTimeout?: boolean): Observable<VerifyPaymentResponse> {
        return this.http.post<VerifyPaymentResponse>(
            `${API_URL}/applications/${applicationId}/payment-methods/primary/verify`,
            {},
            { params: this.buildVerifyParams(qaForceTimeout) },
        );
    }

    verifySecondaryPaymentMethod(applicationId: number, qaForceTimeout?: boolean): Observable<VerifyPaymentResponse> {
        return this.http.post<VerifyPaymentResponse>(
            `${API_URL}/applications/${applicationId}/payment-methods/secondary/verify`,
            {},
            { params: this.buildVerifyParams(qaForceTimeout) },
        );
    }

    private buildSavePaymentMethodParams(qaForcedGiactResponseCode: number | null | undefined): HttpParams | undefined {
        if (environment.production || qaForcedGiactResponseCode == null) {
            return undefined;
        }
        return new HttpParams().set('qa_forced_giact_response_code', String(qaForcedGiactResponseCode));
    }

    private buildVerifyParams(qaForceTimeout: boolean | undefined): HttpParams | undefined {
        if (environment.production || !qaForceTimeout) {
            return undefined;
        }
        return new HttpParams().set('final_override_possible', 'null');
    }
}
